import { t } from "@lingui/macro";
import type {
  CapacityAllocationTypeEnum,
  CheckForAllocationConflictsQuery,
  CheckForAllocationConflictsQueryVariables,
  UsersAllocatedTimeQuery,
  UsersAllocatedTimeQueryVariables,
} from "@src/__generated__/graphql";
import {
  CheckForAllocationConflictsDocument,
  UsersAllocatedTimeDocument,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { secondsToHoursWithMinutes } from "@src/utils/time";
import { upperFirst } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

export type TooltipType = "user-error" | "duration-error" | "valid";
export type TooltipLabelVariant = "task" | "allocation";
export type UserAllocatedTime = NonNullable<
  UsersAllocatedTimeQuery["GetUsersAllocatedTime"]
>[0];
type UserConflictsData = NonNullable<
  CheckForAllocationConflictsQuery["GetUsersAllocationConflicts"]
>[0];

export class UsersAllocatedTimeStore {
  private allocatedTimeFetcher = new FetchHelper<
    UsersAllocatedTimeQuery,
    UsersAllocatedTimeQueryVariables
  >(UsersAllocatedTimeDocument);
  private conflictsFetcher = new FetchHelper<
    CheckForAllocationConflictsQuery,
    CheckForAllocationConflictsQueryVariables
  >(CheckForAllocationConflictsDocument);

  @observable.ref
  allocatedTimeData: UserAllocatedTime[] = [];

  @observable.ref
  conflictsData: UserConflictsData[] = [];

  constructor(private types: CapacityAllocationTypeEnum[]) {
    makeObservable(this);
  }

  @computed
  get isLoadingAllocatedTime(): boolean {
    return this.allocatedTimeFetcher.isLoading.value;
  }

  @action.bound
  async fetchAllocatedTime(
    args: Omit<UsersAllocatedTimeQueryVariables, "types">,
  ) {
    if (!args.from || !args.to || args.user_ids.length === 0) {
      this.allocatedTimeData = [];
      return;
    }

    const [data, error] = await this.allocatedTimeFetcher.fetch({
      from: args.from,
      to: args.to,
      types: this.types,
      user_ids: args.user_ids,
    });
    if (error) return;
    this.allocatedTimeData = data?.GetUsersAllocatedTime ?? [];
  }

  @computed
  get isLoadingConflicts(): boolean {
    return this.conflictsFetcher.isLoading.value;
  }

  @action.bound
  async fetchConflicts(
    args: Omit<CheckForAllocationConflictsQueryVariables, "types">,
  ) {
    if (!args.from || !args.to || args.user_ids.length === 0) {
      this.conflictsData = [];
      return;
    }
    const [data, error] = await this.conflictsFetcher.fetch({
      from: args.from,
      to: args.to,
      user_ids: args.user_ids,
      types: this.types,
      from_time: args.from_time,
      to_time: args.to_time,
      repetition_settings: args.repetition_settings,
    });
    if (error) return;
    this.conflictsData = data?.GetUsersAllocationConflicts ?? [];
  }

  getUserHasConflicts(userId?: string): boolean {
    if (!userId) return false;
    return (
      this.conflictsData.find((u) => u.user_id === userId)?.conflict ?? false
    );
  }

  getUserData(userId?: string): UserAllocatedTime | undefined {
    if (!userId) return undefined;
    return this.allocatedTimeData.find((u) => u.user_id === userId);
  }

  getTooltipType(userId?: string, from?: Date, to?: Date): TooltipType {
    if (userId === undefined) return "user-error";
    if (!from || !to) return "duration-error";
    return "valid";
  }

  getIconName(type: TooltipType) {
    return type === "valid"
      ? ("calendar-check-01" as const)
      : ("calendar-cross-01" as const);
  }

  getIconColor(type: TooltipType) {
    return type === "valid" ? ("green.500" as const) : ("gray.500" as const);
  }

  getTooltipLabel(
    type: TooltipType,
    variant: TooltipLabelVariant = "task",
  ): string | undefined {
    return {
      "user-error":
        variant === "task"
          ? t`To see time-offs, please select user`
          : t`To see allocation intersections, please select users`,
      "duration-error":
        variant === "task"
          ? t`To see time-offs, please set the duration`
          : t`To see allocation intersections, please set the duration for each user`,
      valid:
        variant === "task"
          ? t`User has no time-offs for this duration`
          : t`User has no allocation intersections for this duration`,
    }[type];
  }

  static formatRow(
    item: NonNullable<NonNullable<UserAllocatedTime["dates"]>[0]["data"]>[0],
  ) {
    const baseString = `${upperFirst(item.type.replaceAll("_", " ").toLowerCase())} - ${secondsToHoursWithMinutes(item.value ?? 0)}`;

    if (item.from_time && item.to_time) {
      return `${baseString} (${item.from_time.slice(0, 5)} - ${item.to_time.slice(0, 5)})`;
    }

    return baseString;
  }
}
